/**
 * Context captured alongside every form submission.
 *
 * Before this, a lead arrived in Odoo with name/email/company/phone/message
 * and a free-text `source` string — nothing about where the buyer had been,
 * what they were looking at, or which language they were reading. Sales had
 * no way to tell a seabass hatchery enquiry from a salmon-ova one until they
 * opened the note.
 *
 * Everything here is first-party and derived from the page the user is already
 * on. No cookies are set and no third party is contacted, so this needs no
 * consent gate. The one exception is `visitorToken`, which is only ever
 * present for a visitor who accepted analytics — it is read, never created,
 * here (see readVisitorToken in lib/tracking.ts).
 */

import { marketingAllowed } from "@/lib/consent";
import { readVisitorToken } from "@/lib/tracking";

/**
 * Which form produced the lead. A closed set rather than free text: the Odoo
 * side maps these onto utm.source records, so a typo used to mean a silently
 * unattributed lead. Values are stable identifiers — never translate them.
 */
export const SUBMISSION_SOURCES = {
  contact: "contact",
  servicesSolutions: "services-solutions",
  requestQuotePage: "request-quote-page",
  productDrawer: "product-quote-drawer",
  cart: "cart-quote",
  horeca: "horeca-middle-east",
  oliveFlounderLanding: "olive-flounder-export",
  expoFutureFish: "future-fish-eurasia-2026",
  expoSample: "expo-sample-claim",
  aquariumsHobbyists: "aquariums-and-hobbyists",
  newsletter: "newsletter",
} as const;

export type SubmissionSource = (typeof SUBMISSION_SOURCES)[keyof typeof SUBMISSION_SOURCES];

export interface SubmissionContext {
  /** Closed-set form identifier. */
  source: SubmissionSource;
  /** Path the form was submitted from, without query or hash. */
  pagePath?: string;
  /** Section within the page, where a page has several entry points. */
  pageSection?: string;
  /** Locale the buyer was reading — decides which language sales replies in. */
  locale?: string;
  /** Off-site referrer, if the browser reported one. */
  referrer?: string;
  /** Campaign parameters, if the visit carried them. */
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  /**
   * Keyword and creative. Without these a paid conversion can be traced to a
   * campaign but not to the ad or the search term that actually cost money,
   * which is the level every bid decision is made at.
   */
  utmTerm?: string;
  utmContent?: string;
  /**
   * The advertising network's own click identifier, and which network issued
   * it. This is the only thing that ties a lead back to a specific click:
   * UTM parameters are whatever the campaign author typed and are routinely
   * dropped, mistyped or copied between campaigns, whereas a click id is
   * generated by the network and is the key its own conversion import expects.
   */
  clickId?: string;
  clickIdSource?: ClickIdSource;
  /** Portal account id, when the submitter is signed in. */
  portalUserId?: string;
  /**
   * Anonymous visitor token, present only for a visitor who accepted
   * analytics. Lets Odoo attach the lead to the website.visitor whose
   * pageviews we already hold, so sales sees the journey that led here.
   */
  visitorToken?: string;
}

/** UTM keys we forward. Anything else in the query string is ignored. */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

/**
 * Click identifiers, in priority order, mapped to the network that issues them.
 *
 * Google emits `gclid` normally, and `wbraid`/`gbraid` instead when the click
 * came from an iOS app or web-to-app journey where `gclid` is unavailable —
 * they are alternatives to it, not additions, so at most one is ever present
 * and all three mean "a Google Ads click".
 *
 * Order matters only in the pathological case of a URL carrying several, which
 * happens when a campaign link is pasted into another campaign. Taking the
 * first match is arbitrary but deterministic, which is better than letting
 * object key order decide.
 */
const CLICK_ID_KEYS = [
  ["gclid", "google"],
  ["wbraid", "google"],
  ["gbraid", "google"],
  ["fbclid", "meta"],
] as const;

export type ClickIdSource = (typeof CLICK_ID_KEYS)[number][1];

/**
 * A click id is an opaque token from the ad network. Bounding its length and
 * alphabet keeps a hostile query string from turning into an oversized or
 * structured value in a `crm.lead` field — it is attacker-controlled input that
 * ends up in the CRM, so it is validated like any other.
 */
const CLICK_ID_PATTERN = /^[A-Za-z0-9._-]{1,256}$/;

/** The campaign half of a submission context. */
type CampaignFields = Pick<
  SubmissionContext,
  "utmSource" | "utmMedium" | "utmCampaign" | "utmTerm" | "utmContent" | "clickId" | "clickIdSource"
>;

/**
 * Pull the campaign parameters out of a query string.
 *
 * Split out from the storage handling and exported so it can be tested: the
 * unit suite runs in a node environment with no `window` or `sessionStorage`
 * (vitest.config.ts), and the parsing is the part with rules in it.
 */
export function parseCampaignParams(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const out: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const live = params.get(key);
    if (live) out[key] = live;
  }
  for (const [key, network] of CLICK_ID_KEYS) {
    const live = params.get(key);
    // Rejected rather than truncated: half a click id is not a click id, and
    // sending one that no longer matches anything in Google Ads is worse than
    // sending none, because it looks like attribution that works.
    if (live && CLICK_ID_PATTERN.test(live)) {
      out.click_id = live;
      out.click_id_source = network;
      break;
    }
  }
  return out;
}

/**
 * Record the campaign parameters of the page currently being viewed.
 *
 * **Must be called on every navigation**, from the router — see `__root.tsx`.
 * Before VARS-107 nothing did: the only reader was `readUtm`, which runs when a
 * form is submitted, so the sessionStorage copy it describes was only ever
 * written if the buyer happened to still be on the landing URL at that moment.
 * The comment claimed cross-page persistence and the code delivered none, which
 * is the worst of both — attribution looked implemented while being lost on
 * essentially every real conversion, since almost nobody clicks an ad and
 * submits without navigating first.
 *
 * The tab-scoped copy is first-party and not consent-gated: it reads only the
 * URL the visitor is already on, sets no identifier, does not outlive the tab,
 * and is transmitted only if the visitor chooses to submit a form. The 30-day
 * copy (persistCampaign) requires marketing consent.
 */
export function captureCampaignParams(): void {
  if (typeof window === "undefined") return;
  try {
    const live = parseCampaignParams(window.location.search);
    if (Object.keys(live).length > 0) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(live));
      persistCampaign(live);
    }
  } catch {
    // Private browsing. Attribution degrades; nothing else does.
  }
}

/**
 * Cross-visit attribution (VARS-174), for visitors who granted marketing consent.
 *
 * A B2B buyer typically clicks an ad, leaves, and returns days later to ask for
 * a quote. With the tab-scoped copy alone that lead arrived as "direct". Click
 * ids (gclid, fbclid…) are advertising identifiers, so the copy that outlives
 * the tab is written only with marketing consent, expires after 30 days, and is
 * ignored the moment consent is withdrawn.
 */
const PERSISTED_UTM_KEY = "vars.utm.persist.v1";
export const CAMPAIGN_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function persistCampaign(params: Record<string, string>): void {
  if (!marketingAllowed()) return;
  try {
    localStorage.setItem(PERSISTED_UTM_KEY, JSON.stringify({ at: Date.now(), params }));
  } catch {
    // Storage blocked; tab-scoped attribution still applies.
  }
}

function readPersistedCampaign(): Record<string, string> {
  try {
    if (!marketingAllowed()) {
      localStorage.removeItem(PERSISTED_UTM_KEY);
      return {};
    }
    const raw = localStorage.getItem(PERSISTED_UTM_KEY);
    if (!raw) return {};
    const { at, params } = JSON.parse(raw) as { at: number; params: Record<string, string> };
    if (typeof at !== "number" || Date.now() - at > CAMPAIGN_TTL_MS) {
      localStorage.removeItem(PERSISTED_UTM_KEY);
      return {};
    }
    return params ?? {};
  } catch {
    return {};
  }
}

/**
 * Read the campaign parameters for this visit.
 *
 * Checks the current URL first, then the sessionStorage copy written by
 * `captureCampaignParams` on the landing page — a buyer who arrives on a
 * campaign link and submits three pages later would otherwise lose the
 * attribution entirely.
 *
 * A landing that carries any campaign parameter replaces the stored set
 * wholesale rather than merging into it. That is last-click attribution, which
 * is what the ad networks themselves report on: if the buyer clicks a second ad
 * mid-session, the second click is the one that earned the lead, and merging
 * would leave the first campaign's `gclid` sitting beside the second
 * campaign's `utm_campaign` — a pairing that never existed.
 */
function readUtm(): CampaignFields {
  if (typeof window === "undefined") return {};
  let out: Record<string, string> = {};
  try {
    out = parseCampaignParams(window.location.search);
    if (Object.keys(out).length > 0) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(out));
      persistCampaign(out);
    } else {
      const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
      out = stored ? (JSON.parse(stored) as Record<string, string>) : readPersistedCampaign();
    }
  } catch {
    // Private browsing can throw on sessionStorage. Attribution is a
    // nice-to-have; never let it break a submission.
  }
  return {
    utmSource: out.utm_source,
    utmMedium: out.utm_medium,
    utmCampaign: out.utm_campaign,
    utmTerm: out.utm_term,
    utmContent: out.utm_content,
    clickId: out.click_id,
    clickIdSource: out.click_id_source as ClickIdSource | undefined,
  };
}

const UTM_STORAGE_KEY = "vars.utm.v1";

/**
 * Build the context for a submission.
 *
 * Safe to call during SSR: every browser-only read is guarded, and the result
 * simply carries less information rather than throwing.
 */
export function buildSubmissionContext(
  source: SubmissionSource,
  extra: { pageSection?: string; locale?: string; portalUserId?: string } = {},
): SubmissionContext {
  const onClient = typeof window !== "undefined";
  return {
    source,
    // Path only — the query string can carry campaign parameters and, on some
    // pages, buyer-entered search terms. Those are forwarded deliberately via
    // the UTM fields, not swept up wholesale.
    pagePath: onClient ? window.location.pathname : undefined,
    pageSection: extra.pageSection,
    locale: extra.locale,
    referrer: onClient && document.referrer ? document.referrer : undefined,
    ...readUtm(),
    portalUserId: extra.portalUserId,
    visitorToken: readVisitorToken() ?? undefined,
  };
}
